import { useDebouncedValue } from '@mantine/hooks';
import { DataTable } from 'mantine-datatable';
import { useEffect, useState } from 'react';
import sortBy from 'lodash/sortBy';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import clsx from 'clsx';
import { downloadExcel } from 'react-export-table-to-excel';
import { useDispatch } from 'react-redux';
import { setPageTitle } from '../../store/themeConfigSlice';
import IconFile from '../Icon/IconFile';
import IconPrinter from '../Icon/IconPrinter';
import { useNavigate } from 'react-router-dom';

const DashboardTableWrapper = ({ Dashboarddata, Pagetitle = 'Data Page' }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const PAGE_SIZES = [10, 20, 30, 50, 100];
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [query, setQuery] = useState('');
  const [sortStatus, setSortStatus] = useState({
    columnAccessor: 'id',
    direction: 'asc',
  });
  const [recordsData, setRecordsData] = useState([]);
  const [datacolumns, setDatacolumns] = useState([]);
  const dispatch = useDispatch();

  // Set page title on component mount
  useEffect(() => {
    dispatch(setPageTitle(Pagetitle));
  }, [dispatch, Pagetitle]);

  // Update columns dynamically when Dashboarddata changes
  useEffect(() => {
    if (Dashboarddata.length > 0) {
      const keys = Object.keys(Dashboarddata[0]);

      // Generate columns with custom render function
      const generatedColumns = keys.map((key) => ({
        accessor: key,
        key,
        title: key.replace(/([A-Z])/g, ' $1').trim(),
        sortable: true,
        render: (record) => (
          <div>
            {Array.isArray(record[key])
              ? record[key].map((item, idx) => (
                  <span key={`${record.id}-${key}-${idx}`}>{item}</span> // Ensure unique keys
                ))
              : record[key]}
          </div>
        ), // Custom render for array values
      }));

      setDatacolumns(generatedColumns);

      // Ensure unique key for each record
      const recordsWithKey = Dashboarddata.map((record, index) => ({
        ...record,
        key: record.id || index, // Use 'id' or 'index' if 'id' doesn't exist
      }));

      setRecordsData(sortBy(recordsWithKey, 'id').slice(0, 100)); // Limit to first 100 records initially
    }
  }, [Dashboarddata]);

  // Handle pagination logic
  useEffect(() => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize;
    setRecordsData([...Dashboarddata.slice(from, to)]);
  }, [page, pageSize, Dashboarddata]);

  // Handle sorting
  useEffect(() => {
    const data = sortBy(Dashboarddata, sortStatus.columnAccessor);
    setRecordsData(sortStatus.direction === 'desc' ? data.reverse() : data);
    setPage(1); // Reset to page 1 after sorting
  }, [sortStatus, Dashboarddata]);

  const [debouncedQuery] = useDebouncedValue(query, 200);

  // Filter data based on query
  useEffect(() => {
    const filteredData = Dashboarddata.filter((record) => {
      return (
        debouncedQuery === '' ||
        Object.values(record).some((value) => String(value).toLowerCase().includes(debouncedQuery.toLowerCase()))
      );
    });
    setRecordsData(filteredData);
  }, [debouncedQuery, Dashboarddata]);

  // Export to Excel
  const handleDownloadExcel = () => {
    const header = datacolumns.map((col) => col.title);
    downloadExcel({
      fileName: 'table',
      sheet: 'react-export-table-to-excel',
      tablePayload: {
        header,
        body: Dashboarddata,
      },
    });
  };

  // Export and print methods for CSV/TXT/Print
  const exportTable = (type) => {
    let columns = datacolumns.map((col) => col.title);
    let records = Dashboarddata;
    let filename = 'table';

    let newVariable = window.navigator;

    if (type === 'csv') {
      let coldelimiter = ';';
      let linedelimiter = '\n';
      let result = columns.join(coldelimiter);
      result += linedelimiter;
      records.forEach((item) => {
        result += columns.map((col) => (item[col] ? item[col] : '')).join(coldelimiter);
        result += linedelimiter;
      });
      if (result == null) return;
      var data = 'data:application/csv;charset=utf-8,' + encodeURIComponent(result);
      var link = document.createElement('a');
      link.setAttribute('href', data);
      link.setAttribute('download', filename + '.csv');
      link.click();
    } else if (type === 'print') {
      var rowhtml = `<p>${filename}</p><table>`;
      rowhtml += '<thead><tr>';
      columns.forEach((col) => {
        rowhtml += `<th>${col}</th>`;
      });
      rowhtml += '</tr></thead><tbody>';
      records.forEach((item) => {
        rowhtml += '<tr>';
        columns.forEach((col) => {
          rowhtml += `<td>${item[col]}</td>`;
        });
        rowhtml += '</tr>';
      });
      rowhtml += '</tbody></table>';
      var winPrint = window.open('', '', 'left=0,top=0,width=1000,height=600');
      winPrint.document.write('<title>Print</title>' + rowhtml);
      winPrint.document.close();
      winPrint.focus();
      winPrint.print();
    }
  };

  return (
    <div>
      <div className="panel mt-6 mr-3">
        <div className="flex md:items-center justify-between md:flex-row flex-col mb-2 gap-5">
          <div className="flex items-center flex-wrap">
            <button type="button" onClick={() => exportTable('csv')} className="btn btn-secondary btn-sm m-1">
              <IconFile className="w-5 h-5 ltr:mr-2 rtl:ml-2" />
              CSV
            </button>
            <button type="button" onClick={() => exportTable('txt')} className="btn btn-secondary btn-sm m-1">
              <IconFile className="w-5 h-5 ltr:mr-2 rtl:ml-2" />
              TXT
            </button>
            <button type="button" className="btn btn-secondary btn-sm m-1" onClick={handleDownloadExcel}>
              <IconFile className="w-5 h-5 ltr:mr-2 rtl:ml-2" />
              EXCEL
            </button>
            <button type="button" onClick={() => exportTable('print')} className="btn btn-secondary btn-sm m-1">
              <IconPrinter className="ltr:mr-2 rtl:ml-2" />
              PRINT
            </button>
          </div>
        </div>
        <div className="datatables">
          <DataTable
            highlightOnHover
            className="whitespace-nowrap table-hover"
            records={recordsData}
            height={360}
            columns={datacolumns}
            totalRecords={recordsData.length}
            recordsPerPage={pageSize}
            page={page}
            onPageChange={setPage}
            recordsPerPageOptions={PAGE_SIZES}
            onRecordsPerPageChange={(newPageSize) => setPageSize(newPageSize)}
            sortStatus={sortStatus}
            onSortStatusChange={setSortStatus}
            minHeight={200}
            paginationText={({ from, to, totalRecords }) => `Showing ${from} to ${to} of ${totalRecords} entries`}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardTableWrapper;
